// API Service for Security Scanner Backend
// Connects React frontend to Express backend

// In production (via Cloudflare Tunnel), API is on /api path
// In development, API is on separate port
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001');

// Log API configuration on load
console.log('API Service initialized with base URL:', API_BASE_URL);
console.log('Environment:', process.env.NODE_ENV);

export interface ScanRequest {
  url: string;
  email: string;
  companySize: string;
}

export interface ScanResponse {
  scanId: string;
  accessToken: string;
  status: string;
  message: string;
}

export interface Issue {
  severity: 'critical' | 'warning' | 'informational';
  category: 'website' | 'email';
  title: string;
  description: string;
  risk: string;
  recommendation: string;
  fixTime: string;
}

export interface ScanResult {
  domain: string;
  scanDate: string;
  score: number;
  grade: string;
  critical: number;
  warnings: number;
  passed: number;
  issues: Issue[];
  passed_checks: string[];
  rawResults?: any;
}

// Start a new scan
export async function startScan(data: ScanRequest): Promise<ScanResponse> {
  try {
    console.log('API: Starting scan request to:', `${API_BASE_URL}/api/scan`);
    console.log('API: Request data:', data);
    
    const response = await fetch(`${API_BASE_URL}/api/scan`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
      mode: 'cors',
      credentials: 'omit'
    });

    console.log('API: Response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.error || `Scan failed: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('API: Scan started successfully:', result);
    return result;
  } catch (error) {
    console.error('API: Fetch error:', error);
    // Safari-specific error handling
    if (error instanceof TypeError && error.message === 'Load failed') {
      throw new Error('Unable to connect to scan service. Please check your connection and try again.');
    }
    throw error;
  }
}

// Get scan results
export async function getScanResults(scanId: string, accessToken: string): Promise<ScanResult> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/scan/${scanId}?token=${encodeURIComponent(accessToken)}`, {
      mode: 'cors',
      credentials: 'omit'
    });

    if (!response.ok) {
      if (response.status === 403) {
        throw new Error('Unauthorized access to scan results');
      }
      throw new Error(`Failed to get results: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('API: Error getting scan results:', error);
    throw error;
  }
}

// Poll for scan completion
export async function pollForResults(
  scanId: string,
  accessToken: string,
  onProgress?: (progress: number) => void,
  maxAttempts: number = 60,
  interval: number = 1000
): Promise<ScanResult> {
  for (let i = 0; i < maxAttempts; i++) {
    try {
      const results = await getScanResults(scanId, accessToken);
      
      // If we got results with a domain, scan is complete
      if (results.domain) {
        if (onProgress) onProgress(100);
        return results;
      }
    } catch (error) {
      // Scan not ready yet, continue polling
    }

    // Update progress (rough estimate based on time)
    if (onProgress) {
      const progress = Math.min(95, (i / maxAttempts) * 100);
      onProgress(progress);
    }

    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, interval));
  }

  throw new Error('Scan timeout - results not available after 60 seconds');
}

// Health check
export async function healthCheck(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch (error) {
    return false;
  }
}
